import {
  ApiError,
  appendErrorEntries,
  Control,
  cn,
  Field,
  FieldError,
  Input
} from "@quortex/front-common"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import type { FC } from "react"
import { useFormContext } from "react-hook-form"
import {
  type DeviceEditionFormProps,
  DeviceFiltersGroup,
  DeviceLabels
} from "@/components/DeviceEditionForm"
import { useCdnList } from "@/queries/cdn"
import { useAddSegment, useSegmentList, useUpdateSegment } from "@/queries/segments"
import { SegmentBalancingVariant, useSegmentWizardStore } from "@/stores/segmentWizard"
import type { DeviceFormSchema } from "@/types/form"

const DeviceEditionForm: FC<DeviceEditionFormProps> = ({
  "data-testid": dataTestId,
  segmentId,
  onChange,
  segmentParentUuid,
  className
}) => {
  const form = useFormContext<DeviceFormSchema>()
  const { data: segmentList } = useSuspenseQuery({ ...useSegmentList() })
  const { mutateAsync: addSegmentDryRun } = useMutation(useAddSegment({ dryRun: true }))
  const { mutateAsync: updateSegmentDryRun } = useMutation(useUpdateSegment({ dryRun: true }))
  const { data: balancings } = useSuspenseQuery({ ...useCdnList(), select: data => data.results })
  const wizardParentUuid = useSegmentWizardStore(state => state.parentUuid)
  const parentUuid = segmentParentUuid ?? wizardParentUuid
  const siblings = segmentList.filter(
    segment => "parentUuid" in segment && segment?.parentUuid === parentUuid && !segment.isFallback
  )

  const onDeviceChange = async () => {
    try {
      form.clearErrors("globaldevices")
      onChange?.(form.getValues("devices"))

      if (form.getValues("devices").length !== 0) {
        const payload = {
          balancingMode: SegmentBalancingVariant.PERCENTAGE,
          balancings: [
            {
              cdnUuid: balancings?.[0]?.uuid,
              percentage: 100
            }
          ],
          devices: form.getValues("devices"),
          geolocation: {
            countries: []
          },
          name: `Dry run for Device`,
          parentUuid,
          type: "device" as const
        }

        if (segmentId) await updateSegmentDryRun(payload)
        else await addSegmentDryRun(payload)
      }
    } catch (e) {
      if (e instanceof ApiError) {
        const errorEntries = appendErrorEntries(e.jsonData)
        const [, errorMessage] = errorEntries.find(([path]) => path === "0.message") || [
          "",
          e.message
        ]
        form.setError("globaldevices", { message: errorMessage })
      }
    }
  }

  return (
    <div
      className={cn("grid grid-cols-2 flex-col gap-sm py-md", className)}
      data-testid={dataTestId}
    >
      {DeviceFiltersGroup.map(DeviceFiltersGroup => (
        <Field
          key={`deviceFiltersGroupIndex${DeviceFiltersGroup.name}`}
          className="flex flex-col"
          label={DeviceFiltersGroup.name}
          name="devices"
        >
          {fieldName => (
            <div className="flex flex-col gap-y-xs">
              {DeviceFiltersGroup.filters.map(filterItem => (
                <div
                  key={`deviceFiltersGroupItem${filterItem.value}`}
                  className="flex items-center"
                >
                  <Control
                    as={Input}
                    checked={form.watch(fieldName).includes(filterItem.value)}
                    data-testid={`device-segment-filter-${filterItem.value}`}
                    disabled={filterItem.disabled?.(form.watch(fieldName), siblings) ?? false}
                    id={`DeviceSegmentFilter${filterItem.value}`}
                    name={fieldName}
                    type="checkbox"
                    onChange={async e => {
                      let deviceList: DeviceFormSchema["devices"]
                      if (e.target.checked) {
                        deviceList = [...form.getValues("devices"), filterItem.value]
                      } else {
                        deviceList = [
                          ...form
                            .getValues("devices")
                            .filter(itemName => itemName !== filterItem.value)
                        ]
                      }
                      form.setValue("devices", deviceList || [])
                      await onDeviceChange()
                    }}
                  />
                  <label className="select-none" htmlFor={`DeviceSegmentFilter${filterItem.value}`}>
                    {DeviceLabels[filterItem.value]}
                  </label>
                </div>
              ))}
            </div>
          )}
        </Field>
      ))}
      <FieldError name="globaldevices" />
    </div>
  )
}

export default DeviceEditionForm
